<template>
  <div>
    <div
      v-for="item of items"
      :key="`cost-result-${item.id}`"
      class="rounded p-0 mb-2"
      :class="serviceRowStyles(item)"
    >
      <div
        class="row mx-auto"
        :class="{
          'd-flex align-items-center': !isMobile,
        }"
      >
        <div
          :class="{
            'col-2 px-4 py-2': !isMobile,
            'col py-2': isMobile,
          }"
        >
          <p class="color-gray-6 font-14 mb-0 pl-2">Servicio:</p>
          <p
            class="color-black font-16 mb-0 pl-2"
            :class="
              isServiceDisabled(item) && !isReadOnly
                ? 'color-secondary-7'
                : 'color-black'
            "
          >
            {{ item.serviceName }}
          </p>
        </div>
        <div
          :class="{
            'col-1 row mx-auto px-5 py-2': !isMobile,
            'col py-2': isMobile,
          }"
        >
          <div :class="{ 'col-6': !isMobile }">
            <p class="color-gray-6 font-14 mb-0">Costo:</p>
            <p
              class="font-16 mb-0"
              :class="
                isServiceDisabled(item) && !isReadOnly
                  ? 'color-secondary-7'
                  : 'color-black'
              "
            >
              {{ item.cost | money }}
            </p>
          </div>
        </div>
        <div
          v-if="isTherapyService(item) && item.checked"
          :class="{
            'col-3 row mx-auto px-5 py-2': !isMobile,
            'col py-2': isMobile,
          }"
        >
          <div :class="{ col: !isMobile }">
            <p class="color-gray-6 font-14 mb-0">Intervenciones:</p>
            <p
              class="font-16 mb-0"
              :class="
                isServiceDisabled(item) && !isReadOnly
                  ? 'color-secondary-7'
                  : 'color-black'
              "
            >
              <validation-provider
                v-slot="{ errors }"
                rules="required|min_value:1"
              >
                <custom-input
                  v-if="!readOnly"
                  custom-error-msg="Favor completar la cantidad de intervenciones que se ofrecieron para el periodo seleccionado."
                  :error="errors[0]"
                  type="number"
                  :value="item.visitCount"
                  @input="onVisitCountChange(item, $event)"
                />
              </validation-provider>
              <span v-if="readOnly">{{ item.visitCount }}</span>
            </p>
          </div>
        </div>
        <div
          v-if="isTherapyService(item) && item.checked"
          :class="{
            'col-2 row mx-auto px-5 py-2': !isMobile,
            'col py-2': isMobile,
          }"
        >
          <div :class="{ col: !isMobile }">
            <p class="color-gray-6 font-14 mb-0">Costo Total:</p>
            <p
              class="font-16 mb-0"
              :class="
                isServiceDisabled(item) && !isReadOnly
                  ? 'color-secondary-7'
                  : 'color-black'
              "
            >
              {{ (item.cost * item.visitCount ?? 1) | money }}
            </p>
          </div>
        </div>
        <div
          v-if="isTherapyService(item) && item.checked"
          :class="{
            'col row mx-auto px-5 py-2': !isMobile,
            'col py-2': isMobile,
          }"
        >
          <div :class="{ col: !isMobile }">
            <p
              v-if="item.document || isNew || isReturnToReview"
              class="color-gray-6 font-14 mb-0"
            >
              Certificación de Servicios:
            </p>
            <p
              class="font-16 mb-0"
              :class="
                isServiceDisabled(item) && !isReadOnly
                  ? 'color-secondary-7'
                  : 'color-black'
              "
            >
              <validation-provider
                v-if="!item.document && (isNew || isReturnToReview)"
                v-slot="{ validate, errors }"
                rules="required"
              >
                <custom-file-input
                  v-if="!item.document"
                  ref="fileUpload"
                  accept=".pdf"
                  class="custom-document-input"
                  custom-error-msg="Documento de Certificación de Servicio es requerido para someter solicitud."
                  display-style="normal"
                  :error="errors[0]"
                  label=""
                  :on-download="() => {}"
                  required
                  @after-remove="onServiceFileRemoved(item)"
                  @input="onServiceFileChange(item, $event) || validate($event)"
                />
              </validation-provider>
              <action-button
                v-if="item.document"
                i="fas fa-download"
                text="Descargar"
                variant="success download-btn px-3"
                @click="onDownload(item.document.documentId)"
              />
              <action-button
                v-if="item.document && isReturnToReview"
                i="fas fa-undo-alt"
                text="Reemplazar"
                variant="outline-success ml-2"
                @click="onReplaceDocument(item.id)"
              />
              <input
                :id="`replaceDocumentHidden${item.id}`"
                :ref="`replaceDocumentHidden${item.id}`"
                accept=".pdf"
                style="display:none"
                type="file"
                @change="(e) => onUploadReplaceFile(e, item)"
              />
            </p>
          </div>
        </div>

        <div
          v-if="!isReadOnly"
          class="check-box py-3 px-4"
          :class="checkboxStyles(item)"
        >
          <check-box
            v-model="item.checked"
            class="d-flex justify-content-center align-items-center p-0"
            :class="checkboxStyles(item)"
            :disabled="isServiceDisabled(item)"
            @input="
              onSelectService({
                id: item.id,
                serviceId: item.serviceId,
                checked: $event,
                cost: item.cost,
              })
            "
          />
        </div>
      </div>
    </div>
    <div
      v-if="!isReadOnly"
      class="service-total rounded bg-secondary-12 d-flex justify-content-end align-items-center px-3 py-2"
    >
      <span class="color-black font-16">
        Monto a reembolsar para el periodo seleccionado:
      </span>
      <span class="color-black font-16 font-weight-bold pl-2">
        {{ totalAmount | money }}
      </span>
    </div>
  </div>
</template>

<script>
import ActionButton from 'ActionButton';
import CheckBox from 'CheckBox';
import CustomInput from 'CustomInput';
import CustomFileInput from 'CustomFileInput';
import { serialize as objectToFormData } from 'object-to-formdata';
// constants
import disbursementRequestStatuses from '@/utils/shared/constants/disbursementRequestStatuses';
// services
import documentService from '@/modules/app/api';
import { uploadOrUpdateFile as _uploadOrUpdateFile } from '@/modules/reinbursement/api.js';
import alertMixin from '@/mixins/alert.mixin.js';

export default {
  name: 'RequestServices',
  components: {
    ActionButton,
    CheckBox,
    CustomInput,
    CustomFileInput,
  },
  filters: {
    money(value) {
      return '$' + (Math.round(value * 100) / 100).toFixed(2);
    },
  },
  mixins: [alertMixin],
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    requestStatusId: {
      type: Number,
      default: 0,
    },
    selectedMonthNumber: {
      type: Number,
      default: 0,
    },
    readOnly: {
      type: [String, Boolean],
      default: () => '',
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedServices: [],
      disabledServiceIds: { id: null, value: null, monthNumber: null }, // only used for caching purpose to avoid re-rendering
    };
  },
  computed: {
    isReadOnly() {
      return this.readOnly;
    },
    totalAmount() {
      const total = this.selectedServices.reduce(
        (sum, item) =>
          sum + item.cost * (item.visitCount > 0 ? item.visitCount : 1),
        0
      );
      this.$emit('update-amount', total);
      return total ?? 0;
    },
    isReturnToReview() {
      return (
        this.requestStatusId === disbursementRequestStatuses.DevueltoARevisión
      );
    },
    isNew() {
      return this.requestStatusId === disbursementRequestStatuses.Nuevo;
    },
    serviceRowStyles() {
      return (item) => {
        const isBlocked = this.isServiceDisabled(item);
        let styles = '';

        if (isBlocked && !this.isReadOnly) {
          styles += ' bg-secondary-11';
        } else {
          styles += ' bg-white';
        }

        if (!this.isReadOnly) {
          styles += ' service-option-border';
        }

        return styles;
      };
    },
    checkboxStyles() {
      return (item) => {
        const isDisabled = this.isServiceDisabled(item);
        const isChecked = this.selectedServices.find(
          (x) => x.id === item.id && x.checked
        );
        let styles = '';

        if (isChecked) {
          styles += ' bg-gray-14';
        }
        if (isDisabled) {
          styles += ' disabled-check';
        }

        return styles;
      };
    },
  },
  watch: {
    selectedServices() {
      this.$emit('on-select-services', this.selectedServices);
    },
    items(newVal) {
      this.refreshSelectedItems();
      this.selectedServices = newVal.filter((x) => x.checked);
    },
  },
  mounted() {
    this.selectedServices = this.items.filter((x) => x.checked);
  },
  methods: {
    isServiceDisabled(item) {
      const cacheId = `${item.id}-${this.selectedMonthNumber}`;
      const cache = this.disabledServiceIds[cacheId];
      if (cache) {
        return cache.value;
      }

      let isDisabled = this.items.some(
        (x) => x.id === item.id && !x.isBillable
      );
      if (
        isDisabled &&
        this.isReturnToReview &&
        item.savedServiceDetail?.savedAtMonth === this.selectedMonthNumber
      ) {
        isDisabled = false;
      }

      this.disabledServiceIds[cacheId] = { id: item.id, value: isDisabled };

      return isDisabled;
    },
    refreshSelectedItems() {
      if (!this.items || this.items?.length == 0) return;
      this.items?.forEach((item) => {
        if (
          item.savedServiceDetail?.savedAtMonth === this.selectedMonthNumber &&
          item.checked === undefined
        ) {
          item.checked = true;
          item.visitCount = item.savedServiceDetail?.visitCount;
          item.document = item.savedServiceDetail?.document;
          this.onSelectService({
            id: item.id,
            serviceId: item.serviceId,
            checked: true,
            cost: item.cost,
          });
        }
      });
    },
    onSelectService(selection) {
      const isSelected = this.selectedServices.some(
        (x) => x.id === selection.id
      );
      if (selection.checked && !isSelected) {
        this.selectedServices.push({ ...selection });
      } else if (!selection.checked && isSelected) {
        this.selectedServices = this.selectedServices.filter(
          (x) => x.id !== selection.id
        );
      }
    },
    isTherapyService(item) {
      return item.serviceCategoryId == 2;
    },
    async onDownload(documentId) {
      await documentService.downloadDocument(documentId);
    },
    async onReplaceDocument(id) {
      document.getElementById(`replaceDocumentHidden${id}`).click();
    },
    async onUploadReplaceFile(e, item) {
      if (e?.target?.files && e.target?.files.length > 0) {
        const selectedFile = Array.from(e.target.files)?.map((file) => file);
        let payload = objectToFormData(
          {
            requestId: item.document.requestId,
            documentTypeId: item.document.documentTypeId,
            file: selectedFile[0],
          },
          { indices: true, nullsAsUndefineds: true }
        );

        await _uploadOrUpdateFile(payload).then(({ data }) => {
          if (data) {
            this.ShowToast(
              '',
              'El documento ha sido reemplazado exitosamente.',
              'success'
            );
            let selectedService = this.selectedServices.find(
              (s) => s.id == item.id
            );
            selectedService.file = selectedFile[0];
            this.$emit('on-select-services', this.selectedServices);
          }
        });

        this.$refs.replaceDocumentFile.value = null;
      }
    },
    onServiceFileChange(item, file) {
      let selectedService = this.selectedServices.find((s) => s.id == item.id);
      selectedService.file = file;
      this.$emit('on-select-services', this.selectedServices);
    },
    onServiceFileRemoved(item) {
      this.removeDocumentData(item);
    },
    removeDocumentData(item) {
      item.document = null;
      let selectedService = this.selectedServices.find((s) => s.id == item.id);
      selectedService.file = null;
      selectedService.document = null;
      this.$emit('on-select-services', this.selectedServices);
    },
    onVisitCountChange(item, event) {
      let selectedService = this.selectedServices.find((s) => s.id == item.id);
      item.visitCount = parseInt(event ?? 1);
      selectedService.visitCount = parseInt(event ?? 1);
      this.selectedServices = this.selectedServices.map((x) => {
        if (x.id === item.id) {
          x = selectedService;
        }
        return x;
      });
      this.$emit('on-select-services', this.selectedServices);
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/shared/scss/config/_colors.scss';

.service-option-border {
  border: 1px solid map-get($colors, 'gray-13');
}
.check-box {
  display: flex;
  justify-content: end;
  align-items: center;
  border-left: 1px solid map-get($colors, 'gray-13');
}
::v-deep .input-group .option-input + label span {
  margin: 0 !important;
}
::v-deep .disabled-check .input-group .option-input + label span {
  margin: 0 !important;
  background-color: map-get($colors, 'secondary-5') !important;
}
</style>
